import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from '../utils/toast';
import { fmtDate, fmtWeekday } from "../utils/datetime";
import PageLayout from "../components/PageLayout";
import { TourPlanPageProps } from "../components/common/types";
import { useWorkCalendar } from "../hooks/useWorkCalendar";
import { TourPlanType } from "../types/tourPlan";
import EmployeeMain from "../types/employee";
import { getAllEmployees } from "../api/employee";
import { useAuth } from "../context/AuthContext";
import {
    ChevronDown,
    Calendar,
    MapPin,
    User,
    Search,
    Filter,
    X,
    Clock,
    AlertCircle,
    CheckCircle,
    FileText,
    Timer,
    ThumbsUp,
    ThumbsDown,
    Plus,
    Edit3,
    Trash2,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { updateTourPlan, deleteTourPlan, createSingleTourPlan, updateSingleTourPlan, updateReschedule } from "../api/tour-plan";
import { getLocationsByEmployee } from "../api/tour-plan";

const TourPlansPage: React.FC<TourPlanPageProps> = ({ onLogout, onNavigate }) => {
    const { user } = useAuth();

    const {
        loading: tourPlanLoading,
        error,
        tourPlans,
        fetchTourPlans,
    } = useWorkCalendar();

    const [employees, setEmployees] = useState<EmployeeMain[]>([]);
    const [employeeLoading, setEmployeeLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [planSearch, setPlanSearch] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        status: "",
        date: "",
        hasDeviation: "all"
    });
    const PLANS_PER_PAGE = 10;
    const [plansPage, setPlansPage] = useState(1);
    const [approvingId, setApprovingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [rescheduleActionId, setRescheduleActionId] = useState<number | null>(null);

    // ── Create / Edit modal ───────────────────────────────────────────
    type PlanModalMode = "create" | "edit" | "recreate";
    const [planModal, setPlanModal] = useState<{
        open: boolean;
        mode: PlanModalMode;
        plan?: TourPlanType;
    }>({ open: false, mode: "create" });
    const [modalDate, setModalDate] = useState("");
    const [modalLocationId, setModalLocationId] = useState<number | "">("");
    const [modalLocations, setModalLocations] = useState<{ id: number; name: string }[]>([]);
    const [modalSaving, setModalSaving] = useState(false);
    const [modalError, setModalError] = useState("");

    const openCreateModal = async () => {
        setPlanModal({ open: true, mode: "create" });
        setModalDate("");
        setModalLocationId("");
        setModalError("");
        await loadLocationsForModal();
    };

    const openEditModal = async (plan: TourPlanType) => {
        setPlanModal({ open: true, mode: "edit", plan });
        setModalDate(plan.tour_plan_date ?? "");
        setModalLocationId(plan.location_id ?? "");
        setModalError("");
        await loadLocationsForModal();
    };

    const openRecreateModal = async (plan: TourPlanType) => {
        setPlanModal({ open: true, mode: "recreate", plan });
        setModalDate(plan.tour_plan_date ?? "");
        setModalLocationId("");
        setModalError("");
        await loadLocationsForModal();
    };

    const loadLocationsForModal = async () => {
        if (!selectedEmployee) return;
        try {
            const res = await getLocationsByEmployee(selectedEmployee);
            setModalLocations(res.data?.data ?? []);
        } catch {
            setModalLocations([]);
        }
    };

    const handleModalSave = async () => {
        if (!modalDate || !modalLocationId || !selectedEmployee || !user?.tenant_id) {
            setModalError("Please fill in all required fields.");
            return;
        }
        setModalSaving(true);
        setModalError("");
        try {
            if (planModal.mode === "edit" && planModal.plan) {
                await updateSingleTourPlan(planModal.plan.id, Number(modalLocationId), user.tenant_id);
            } else {
                // create or recreate — backend auto-removes rejected plan
                await createSingleTourPlan(user.tenant_id, selectedEmployee, Number(modalLocationId), modalDate, "draft");
            }
            setPlanModal({ open: false, mode: "create" });
            await fetchTourPlans(selectedEmployee, user.tenant_id);
        } catch (err: any) {
            setModalError(err?.response?.data?.message ?? "Failed to save tour plan.");
        } finally {
            setModalSaving(false);
        }
    };

    const handleDeleteDraft = async (plan: TourPlanType) => {
        const confirmed = await toast.confirm(
            `Delete draft tour plan for ${plan.tour_plan_date}?`,
            'Delete tour plan?',
            'Delete',
        );
        if (!confirmed) return;
        setDeletingId(plan.id);
        try {
            await deleteTourPlan(plan.id);
            if (selectedEmployee && user?.tenant_id) {
                await fetchTourPlans(selectedEmployee, user.tenant_id);
            }
            toast.success('Tour plan deleted.');
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? "Failed to delete tour plan.");
        } finally {
            setDeletingId(null);
        }
    };

    const handleRescheduleAction = async (
        rescheduleId: number,
        status: "approved" | "rejected",
    ) => {
        if (!user?.tenant_id) return;
        setRescheduleActionId(rescheduleId);
        try {
            await updateReschedule(user.tenant_id, rescheduleId, status);
            if (selectedEmployee) await fetchTourPlans(selectedEmployee, user.tenant_id);
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? "Failed to update reschedule.");
        } finally {
            setRescheduleActionId(null);
        }
    };

    const handleApproval = async (planId: number, status: "approved" | "rejected") => {
        if (!user?.tenant_id) return;
        setApprovingId(planId);
        try {
            await updateTourPlan([{
                id: planId,
                tenant_id: user.tenant_id,
                status,
                approved_by: user.id,
            } as any]);
            await fetchTourPlans(selectedEmployee!, user.tenant_id);
        } catch (err) {
            console.error("Failed to update tour plan:", err);
        } finally {
            setApprovingId(null);
        }
    };

    /* -----------------------------
        Load Employees
    ------------------------------ */
    const loadEmployees = useCallback(async () => {
        if (!user?.tenant_id) return;

        setEmployeeLoading(true);
        try {
            const res = await getAllEmployees(user.tenant_id);
            setEmployees(res);
        } catch (err) {
            console.error("Failed to load employees", err);
        } finally {
            setEmployeeLoading(false);
        }
    }, [user?.tenant_id]);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);

    /* -----------------------------
        Fetch Tour Plans
    ------------------------------ */
    useEffect(() => {
        if (selectedEmployee && user?.tenant_id) {
            fetchTourPlans(selectedEmployee, user.tenant_id);
        }
    }, [selectedEmployee, user?.tenant_id]);

    /* -----------------------------
        Filters
    ------------------------------ */
    const filteredEmployees = useMemo(() => {
        return employees.filter(emp =>
            emp.user.name.toLowerCase().includes(employeeSearch.toLowerCase())
        );
    }, [employees, employeeSearch]);

    const filteredPlans = useMemo(() => {
        return tourPlans.filter(plan => {
            // Search filter
            const searchMatch = planSearch === "" ||
                plan.location?.name?.toLowerCase().includes(planSearch.toLowerCase()) ||
                plan.status?.toLowerCase().includes(planSearch.toLowerCase()) ||
                plan.tour_plan_date?.toLowerCase().includes(planSearch.toLowerCase())

            // Additional filters
            const statusMatch = !filters.status || plan.status === filters.status;
            const dateMatch = !filters.date || plan.tour_plan_date === filters.date;
            const deviationMatch = filters.hasDeviation === "all" ||
                (filters.hasDeviation === "yes" && plan.deviation) ||
                (filters.hasDeviation === "no" && !plan.deviation);

            return searchMatch && statusMatch && dateMatch && deviationMatch;
        });
    }, [tourPlans, planSearch, filters]);

    // Get unique filter values
    const filterOptions = useMemo(() => {
        const dates = Array.from(new Set(tourPlans.map(plan => plan.tour_plan_date).filter(Boolean)));
        const statuses = Array.from(new Set(tourPlans.map(plan => plan.status).filter(Boolean)));

        return { dates, statuses };
    }, [tourPlans]);

    const handleFilterChange = (filterType: string, value: string) => {
        setFilters(prev => ({ ...prev, [filterType]: value }));
    };

    const clearAllFilters = () => {
        setPlanSearch("");
        setFilters({
            status: "",
            date: "",
            hasDeviation: "all"
        });
    };

    const hasActiveFilters = planSearch || Object.values(filters).some(filter =>
        filter !== "" && filter !== "all"
    );

    // Reset to page 1 whenever the employee, search, or filters change so
    // stale page numbers don't hide results.
    useEffect(() => {
        setPlansPage(1);
    }, [selectedEmployee, planSearch, filters]);

    const plansTotalPages = Math.max(1, Math.ceil(filteredPlans.length / PLANS_PER_PAGE));
    const safePlansPage = Math.min(plansPage, plansTotalPages);
    const paginatedPlans = filteredPlans.slice(
        (safePlansPage - 1) * PLANS_PER_PAGE,
        safePlansPage * PLANS_PER_PAGE
    );

    const getStatusIcon = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'approved':
            case 'completed':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'pending':
                return <Timer className="w-4 h-4 text-yellow-600" />;
            case 'rejected':
            case 'cancelled':
                return <X className="w-4 h-4 text-red-600" />;
            default:
                return <Clock className="w-4 h-4 text-gray-400" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'approved':
            case 'completed':
                return "bg-green-100 text-green-800";
            case 'pending':
                return "bg-yellow-100 text-yellow-800";
            case 'rejected':
            case 'cancelled':
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const selectedEmployeeData = employees.find(e => e.id === selectedEmployee);

    if (employeeLoading) {
        return (
            <PageLayout onLogout={onLogout} currentPage="tour-plans" onNavigate={onNavigate}>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout onLogout={onLogout} currentPage="tour-plans" onNavigate={onNavigate}>
            <div className="p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Tour Plans</h1>
                                <p className="text-gray-600 mt-1">View and manage employee tour plans</p>
                            </div>
                            {selectedEmployee && (
                                <button
                                    onClick={openCreateModal}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Tour Plan
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Employee Selector */}
                    <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Select Employee
                        </label>

                        <div className="relative">
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="w-full md:w-96 bg-white border border-gray-300 rounded-lg px-4 py-3 flex justify-between items-center hover:border-gray-400 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <User className="h-5 w-5 text-gray-400" />
                                    {selectedEmployeeData ? (
                                        <div className="text-left">
                                            <p className="font-medium text-gray-900">
                                                {selectedEmployeeData.user.name}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {selectedEmployeeData.code}
                                            </p>
                                        </div>
                                    ) : (
                                        <span className="text-gray-500">Select an employee</span>
                                    )}
                                </div>
                                <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                            </button>

                            {isDropdownOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setIsDropdownOpen(false)}
                                    />

                                    <div className="absolute z-20 mt-1 w-full md:w-96 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                        {/* Employee Search */}
                                        <div className="p-3 border-b bg-gray-50">
                                            <div className="relative">
                                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                <input
                                                    placeholder="Search employees..."
                                                    value={employeeSearch}
                                                    onChange={(e) => setEmployeeSearch(e.target.value)}
                                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>

                                        <div className="max-h-64 overflow-auto">
                                            {filteredEmployees.length === 0 ? (
                                                <div className="p-4 text-center text-gray-500">
                                                    No employees found
                                                </div>
                                            ) : (
                                                filteredEmployees.map(emp => (
                                                    <button
                                                        key={emp.id}
                                                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${selectedEmployee === emp.id ? 'bg-blue-50' : ''}`}
                                                        onClick={() => {
                                                            setSelectedEmployee(emp.id);
                                                            setEmployeeSearch("");
                                                            setIsDropdownOpen(false);
                                                        }}
                                                    >
                                                        <p className="font-medium text-gray-900">{emp.user.name}</p>
                                                        <p className="text-sm text-gray-500">{emp.code}</p>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {selectedEmployee ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            {/* Header with Search and Filters */}
                            <div className="p-4 border-b border-gray-200 bg-gray-50">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">
                                            Tour Plans for {selectedEmployeeData?.user.name}
                                        </h2>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Showing {filteredPlans.length} of {tourPlans.length} tour plans
                                            {hasActiveFilters && ' (filtered)'}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        {/* Search Bar */}
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search tour plans..."
                                                value={planSearch}
                                                onChange={(e) => setPlanSearch(e.target.value)}
                                                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                                            />
                                        </div>

                                        {/* Filter Toggle Button */}
                                        <button
                                            onClick={() => setShowFilters(!showFilters)}
                                            className={`flex items-center px-4 py-2 border rounded-lg transition-colors ${showFilters || hasActiveFilters
                                                ? 'bg-blue-50 border-blue-300 text-blue-700'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            <Filter className="w-4 h-4 mr-2" />
                                            Filters
                                            {hasActiveFilters && (
                                                <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                                                    Active
                                                </span>
                                            )}
                                        </button>

                                        {/* Clear Filters Button */}
                                        {hasActiveFilters && (
                                            <button
                                                onClick={clearAllFilters}
                                                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                                            >
                                                <X className="w-4 h-4 mr-1" />
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Filter Dropdowns */}
                                {showFilters && (
                                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Status
                                            </label>
                                            <select
                                                value={filters.status}
                                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="">All Statuses</option>
                                                {filterOptions.statuses.map(status => (
                                                    <option key={status} value={status}>
                                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Date
                                            </label>
                                            <input
                                                type="date"
                                                value={filters.date}
                                                onChange={(e) => handleFilterChange('date', e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Deviation
                                            </label>
                                            <select
                                                value={filters.hasDeviation}
                                                onChange={(e) => handleFilterChange('hasDeviation', e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="all">All Plans</option>
                                                <option value="yes">With Deviation</option>
                                                <option value="no">Without Deviation</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Loading State */}
                            {tourPlanLoading && (
                                <div className="p-8 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="text-gray-500 mt-2">Loading tour plans...</p>
                                </div>
                            )}

                            {/* Error State */}
                            {error && (
                                <div className="p-8 text-center">
                                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                                    <p className="text-red-600">{error}</p>
                                </div>
                            )}

                            {/* Table */}
                            {!tourPlanLoading && !error && (
                                <div className="overflow-x-auto">
                                    {filteredPlans.length === 0 ? (
                                        <div className="p-8 text-center">
                                            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500">
                                                {hasActiveFilters
                                                    ? 'No tour plans match the current filters.'
                                                    : 'No tour plans found for this employee.'}
                                            </p>
                                        </div>
                                    ) : (
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Date
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Location
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Deviation
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Reschedule
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Created
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {paginatedPlans.map((plan) => (
                                                    <tr key={plan.id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {fmtDate(plan.tour_plan_date)}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {fmtWeekday(plan.tour_plan_date)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center">
                                                                <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {plan.location?.name || 'No location'}
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                {getStatusIcon(plan.status)}
                                                                <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(plan.status)}`}>
                                                                    {plan.status || 'Unknown'}
                                                                </span>
                                                                <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full}`}>
                                                                    {plan.approved_by_user ? `Approved by ${plan.approved_by_user.name}` : ""}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {plan.deviation ? (
                                                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                                                    <AlertCircle className="h-3 w-3 mr-1" />
                                                                    Deviation Requested
                                                                </span>
                                                            ) : (
                                                                <span className="text-sm text-gray-500">—</span>
                                                            )}
                                                        </td>

                                                        {/* Reschedule cell */}
                                                        <td className="px-6 py-4">
                                                            {plan.reschedule ? (
                                                                <div className="space-y-1.5">
                                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                                            plan.reschedule.status === "approved"  ? "bg-green-100 text-green-700" :
                                                                            plan.reschedule.status === "rejected"  ? "bg-red-100 text-red-700"   :
                                                                            "bg-blue-100 text-blue-700"
                                                                        }`}>
                                                                            {plan.reschedule.status}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-xs text-gray-500">
                                                                        {plan.reschedule.old_date} → {plan.reschedule.new_date}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {plan.reschedule.old_location?.name ?? "?"} → {plan.reschedule.new_location?.name ?? "?"}
                                                                    </p>
                                                                    {plan.reschedule.status === "submitted" && (
                                                                        <div className="flex gap-1.5 mt-1">
                                                                            <button
                                                                                onClick={() => handleRescheduleAction(plan.reschedule!.id, "approved")}
                                                                                disabled={rescheduleActionId === plan.reschedule.id}
                                                                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                                                                            >
                                                                                <ThumbsUp className="w-3 h-3" /> Approve
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleRescheduleAction(plan.reschedule!.id, "rejected")}
                                                                                disabled={rescheduleActionId === plan.reschedule.id}
                                                                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-50 text-red-600 border border-red-300 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                                                                            >
                                                                                <ThumbsDown className="w-3 h-3" /> Reject
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-sm text-gray-500">—</span>
                                                            )}
                                                        </td>

                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {fmtDate(plan.created_at)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex gap-2 flex-wrap">
                                                                {/* SUBMITTED: Approve + Reject */}
                                                                {plan.status === 'submitted' && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleApproval(plan.id, 'approved')}
                                                                            disabled={approvingId === plan.id}
                                                                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                                                                        >
                                                                            <ThumbsUp className="w-3 h-3" />
                                                                            Approve
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleApproval(plan.id, 'rejected')}
                                                                            disabled={approvingId === plan.id}
                                                                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 border border-red-300 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                                                                        >
                                                                            <ThumbsDown className="w-3 h-3" />
                                                                            Reject
                                                                        </button>
                                                                    </>
                                                                )}

                                                                {/* DRAFT: Edit + Delete */}
                                                                {plan.status === 'draft' && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => openEditModal(plan)}
                                                                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-100 transition-colors"
                                                                        >
                                                                            <Edit3 className="w-3 h-3" />
                                                                            Edit
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleDeleteDraft(plan)}
                                                                            disabled={deletingId === plan.id}
                                                                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 border border-red-300 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                                                                        >
                                                                            <Trash2 className="w-3 h-3" />
                                                                            Delete
                                                                        </button>
                                                                    </>
                                                                )}

                                                                {/* REJECTED: Re-create */}
                                                                {plan.status === 'rejected' && (
                                                                    <button
                                                                        onClick={() => openRecreateModal(plan)}
                                                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-300 rounded-lg hover:bg-green-100 transition-colors"
                                                                    >
                                                                        <RefreshCw className="w-3 h-3" />
                                                                        Re-create
                                                                    </button>
                                                                )}

                                                                {plan.status === 'approved' && (
                                                                    <span className="text-xs text-gray-400 italic">Approved</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {/* Footer */}
                            {!tourPlanLoading && !error && filteredPlans.length > 0 && (
                                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                    <p className="text-sm text-gray-600">
                                        Total Tour Plans: <span className="font-medium">{tourPlans.length}</span>
                                        {hasActiveFilters && (
                                            <span className="ml-4">
                                                Filtered: <span className="font-medium">{filteredPlans.length}</span>
                                            </span>
                                        )}
                                        <span className="ml-4">
                                            Showing{' '}
                                            <span className="font-medium">
                                                {(safePlansPage - 1) * PLANS_PER_PAGE + 1}–{Math.min(safePlansPage * PLANS_PER_PAGE, filteredPlans.length)}
                                            </span>
                                        </span>
                                    </p>

                                    {plansTotalPages > 1 && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => setPlansPage(safePlansPage - 1)}
                                                disabled={safePlansPage === 1}
                                                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <ChevronLeft className="w-4 h-4" />
                                            </button>

                                            {Array.from({ length: plansTotalPages }, (_, i) => i + 1)
                                                .filter(p => p === 1 || p === plansTotalPages || Math.abs(p - safePlansPage) <= 1)
                                                .map((p, idx, arr) => (
                                                    <Fragment key={p}>
                                                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                                                            <span className="px-1 text-gray-400 text-sm">…</span>
                                                        )}
                                                        <button
                                                            onClick={() => setPlansPage(p)}
                                                            className={`min-w-[32px] px-2 py-1.5 text-sm rounded-lg ${
                                                                p === safePlansPage
                                                                    ? 'bg-blue-600 text-white font-medium'
                                                                    : 'border border-gray-300 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            {p}
                                                        </button>
                                                    </Fragment>
                                                ))}

                                            <button
                                                onClick={() => setPlansPage(safePlansPage + 1)}
                                                disabled={safePlansPage === plansTotalPages}
                                                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <ChevronRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                            <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No Employee Selected
                            </h3>
                            <p className="text-gray-600 max-w-md mx-auto">
                                Please select an employee from the dropdown above to view their tour plans
                            </p>
                        </div>
                    )}

                </div>
            </div>
            {/* ── Create / Edit / Re-create modal ──────────────────── */}
            {planModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {planModal.mode === "edit"     ? "Edit Draft Tour Plan" :
                                 planModal.mode === "recreate" ? "Re-create Tour Plan"  :
                                                                 "Add Tour Plan"}
                            </h3>
                            <button onClick={() => setPlanModal({ open: false, mode: "create" })}
                                className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Employee */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                            <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                                {employees.find(e => e.id === selectedEmployee)?.user.name ?? "—"}
                            </p>
                        </div>

                        {/* Date */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={modalDate}
                                onChange={e => setModalDate(e.target.value)}
                                disabled={planModal.mode === "edit"}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                            />
                        </div>

                        {/* Location */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Location <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={modalLocationId}
                                onChange={e => setModalLocationId(Number(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">-- Select Location --</option>
                                {modalLocations.map(loc => (
                                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                                ))}
                            </select>
                        </div>

                        {modalError && (
                            <p className="text-red-600 text-sm mb-3">{modalError}</p>
                        )}

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setPlanModal({ open: false, mode: "create" })}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleModalSave}
                                disabled={modalSaving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                            >
                                {modalSaving ? "Saving..." : planModal.mode === "edit" ? "Update" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
};

export default TourPlansPage;